import type { DriveStep } from 'driver.js';

import orderV2Reference from '@/assets/img/order-v2-reference.png';
import type { OrderCreationStepKey } from '@/util/vexo';

// Contextual product-tour definitions for the OrderFlow V2 wizard.
//
// Each wizard step (keyed by the same `OrderCreationStepKey` the wizard already
// uses for analytics) maps to an ordered list of driver.js steps. Elements are
// targeted by stable `data-tour="orderflow-<name>"` attributes rather than
// class names / DOM structure, so the tour survives markup changes.
//
// `useOrderFlowTour` filters out any step whose element is not currently in the
// DOM before driving, so anchors that only render for certain flows (markup
// pay-rate, non-LTA staffing choices, the invite substep) are skipped
// automatically when absent.

/** Build the CSS selector for a tour anchor name. */
export const tourSelector = (name: string) => `[data-tour="orderflow-${name}"]`;

/**
 * A driver.js step plus an opt-in flag for anchors that may legitimately be
 * absent but whose popover still has something to say (e.g. an image crop that
 * demonstrates a feature the user has not unlocked yet). `useOrderFlowTour`
 * keeps these as centered, unanchored popovers instead of dropping them.
 */
export type TourStep = DriveStep & { keepWhenAnchorMissing?: boolean };

// --- Shared chrome anchors (present on every wizard step) ---

const stepperIntro: DriveStep = {
  element: tourSelector('stepper'),
  popover: {
    title: 'Where you are',
    description:
      'These are the steps to create an order. The highlighted one is your current step — finish it, then continue.',
    side: 'bottom',
    align: 'center',
  },
};

const continueOutro: DriveStep = {
  element: tourSelector('next'),
  popover: {
    title: 'Move forward',
    description:
      'When this step looks good, use this button to continue to the next step.',
    side: 'top',
    align: 'end',
  },
};

const placeOrderOutro: DriveStep = {
  element: tourSelector('next'),
  popover: {
    title: 'Place your order',
    description: 'When everything looks right, this button submits your order.',
    side: 'top',
    align: 'end',
  },
};

const helpOutro: DriveStep = {
  element: tourSelector('help'),
  popover: {
    title: 'Reopen this tour',
    description:
      'Stuck later on? Click this help icon anytime to see these tips for the step you’re on.',
    side: 'bottom',
    align: 'end',
  },
};

// --- Per-step anchors ---

const jobTypeAnchors: DriveStep[] = [
  {
    element: tourSelector('job-type-categories'),
    popover: {
      title: 'Pick a job category',
      description:
        'Start by choosing the category of work you need — it filters the list of specific skills below.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: tourSelector('job-type-skills'),
    popover: {
      title: 'Choose the skill',
      description:
        'Pick the exact skill for this job. Your choice also pre-fills the default uniform and instructions.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: tourSelector('job-type-location'),
    popover: {
      title: 'Set the location',
      description:
        'Set where the job takes place — search your saved locations or look up a new address.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: tourSelector('job-type-directions'),
    popover: {
      title: 'How to get there',
      description:
        'Tell workers where to go and what to do when they arrive — which entrance, who to ask for, where to park.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: tourSelector('job-type-browse-saved'),
    popover: {
      title: 'Reuse your directions',
      description:
        'Click Browse saved to pick directions you wrote before, so you don’t retype them every order. Save adds the current text to that list.',
      side: 'top',
      align: 'end',
    },
  },
];

const scheduleAnchors: DriveStep[] = [
  {
    element: tourSelector('schedule-calendar'),
    popover: {
      title: 'Add your shifts',
      description:
        'Click one or more dates to add shifts, then set the times and how many workers you need.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: tourSelector('schedule-shift-list'),
    popover: {
      title: 'Review the shifts',
      description:
        'Every shift you add shows up here with running hours and an estimated total — edit or remove any of them.',
      side: 'left',
      align: 'start',
    },
  },
];

const staffingAnchors: DriveStep[] = [
  {
    element: tourSelector('staffing-heading'),
    popover: {
      title: 'Staff your shifts',
      description: 'Choose how you want to fill the shifts you just scheduled.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: tourSelector('staffing-strategy'),
    popover: {
      title: 'Pick a fill mode',
      description:
        'Post the job openly to all qualified workers, or hand-pick workers and send them invitations.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: tourSelector('staffing-autofill'),
    popover: {
      title: 'Auto-fill',
      description:
        'Turn this on to open the job to other qualified workers if your invited workers aren’t available.',
      side: 'top',
      align: 'start',
    },
  },
];

const invitesAnchors: DriveStep[] = [
  {
    element: tourSelector('invite-shifts'),
    popover: {
      title: 'Choose shifts',
      description: 'Select which shifts you’re inviting workers to.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: tourSelector('invite-workers'),
    popover: {
      title: 'Pick your workers',
      description:
        'Search and choose the workers to hire or invite for these shifts.',
      side: 'top',
      align: 'start',
    },
  },
];

const instructionsAnchors: DriveStep[] = [
  {
    element: tourSelector('instructions-dress-code'),
    popover: {
      title: 'Set the dress code',
      description:
        'Choose the dress code — tap a uniform tile to set what staff should wear on this job.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: tourSelector('instructions-field'),
    popover: {
      title: 'Add instructions',
      description:
        'Describe the job responsibilities and any special instructions for the shift here.',
      side: 'top',
      align: 'start',
    },
  },
];

const payRateAnchors: DriveStep[] = [
  {
    element: tourSelector('pay-rate-input'),
    popover: {
      title: 'Set the pay rate',
      description:
        'Type the hourly pay rate you want to offer for this skill — it drives everything else on the step.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: tourSelector('pay-rate-feedback'),
    popover: {
      title: 'Rate feedback',
      description:
        'As you type, this live indicator tells you whether your rate is below minimum, competitive, or great.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: tourSelector('pay-rate-range'),
    popover: {
      title: 'Market range',
      description:
        'This shows the going market pay range for this skill and location, so you can pick a rate that attracts candidates.',
      side: 'top',
      align: 'start',
    },
  },
];

const summaryAnchors: DriveStep[] = [
  {
    element: tourSelector('review-job-list'),
    popover: {
      title: 'Your jobs',
      description:
        'Each job in your order appears here — check the skill, schedule, worker counts, and pay rates.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: tourSelector('review-summary'),
    popover: {
      title: 'Order summary',
      description:
        'Your order at a glance — the total hours and estimated cost before you place it.',
      side: 'left',
      align: 'start',
    },
  },
  {
    element: tourSelector('review-payment'),
    popover: {
      title: 'Payment method',
      description:
        'The payment method that will be charged. If none is set, you’ll be prompted to add one here.',
      side: 'left',
      align: 'start',
    },
  },
];

/**
 * Ordered driver.js steps per wizard step. Each tour opens by orienting the
 * user on the stepper, walks the step’s key controls, then closes on the
 * primary action and the help button.
 */
export const ORDERFLOW_TOUR_STEPS: Record<OrderCreationStepKey, DriveStep[]> = {
  job_type: [stepperIntro, ...jobTypeAnchors, continueOutro, helpOutro],
  schedule: [stepperIntro, ...scheduleAnchors, continueOutro, helpOutro],
  staffing: [stepperIntro, ...staffingAnchors, continueOutro, helpOutro],
  invites: [stepperIntro, ...invitesAnchors, continueOutro, helpOutro],
  instructions: [
    stepperIntro,
    ...instructionsAnchors,
    continueOutro,
    helpOutro,
  ],
  pay_rate: [stepperIntro, ...payRateAnchors, continueOutro, helpOutro],
  summary: [stepperIntro, ...summaryAnchors, placeOrderOutro, helpOutro],
};

// --- Entry screen (pre-wizard landing at /orders-v2/create) ---

const entryAnchors: DriveStep[] = [
  {
    element: tourSelector('entry-title'),
    popover: {
      title: 'Create a new order',
      description:
        'This is where every order starts. Here’s a quick tour of the setup before you open the full wizard.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: tourSelector('entry-client'),
    popover: {
      title: 'Pick the client',
      description:
        'Start typing to search your directory and choose the client you’re ordering for.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: tourSelector('entry-billing'),
    popover: {
      title: 'Choose a billing account',
      description:
        'Select the billing account to charge — the order can’t start until one with a valid payment method is set.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: tourSelector('entry-start'),
    popover: {
      title: 'Start the order',
      description:
        'Once your client and billing account are set, hit Start order to open the full order wizard.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: tourSelector('entry-repeat'),
    popover: {
      title: 'Reorder in one click',
      description:
        'Ordered something before? Pick a past order here to prefill the whole thing instantly.',
      side: 'top',
      align: 'start',
    },
  },
];

/**
 * Tour for the order-creation entry/landing screen (`/orders-v2/create`),
 * shown before the wizard opens. Conditional anchors (the client selector is
 * tenant-admin only; repeat orders appear only once a billing account with
 * saved templates is picked) are dropped by `useOrderFlowTour` when absent.
 */
export const ENTRY_TOUR_STEPS: DriveStep[] = [...entryAnchors, helpOutro];

// --- First-entry image tour (auto-run once per user) ---
//
// Same entry anchors, but each popover shows a cropped snippet of the
// `order-v2-reference.png` mockup (a filled-out example of this screen) above
// the instruction, so a first-time user sees what a completed field looks like.
// The "?" button keeps replaying the text-only ENTRY_TOUR_STEPS above; this
// richer variant is only driven automatically on a user's first visit.

// Natural width of order-v2-reference.png (1662x1206). Crops below are in the
// image's native pixel space and scaled into the popover by `cropStyle`.
const REFERENCE_IMAGE_WIDTH = 1662;
// Rendered width of the crop window inside the popover. The image-tour popover
// is widened to fit this (see `.orderflow-tour-image` in orderflowTour.css).
const CROP_RENDER_WIDTH = 320;

type Crop = { x: number; y: number; w: number; h: number };

// Crop rectangles (native image px) validated visually against the mockup.
const ENTRY_IMAGE_CROPS: Record<
  'client' | 'billing' | 'start' | 'repeat',
  Crop
> = {
  client: { x: 80, y: 193, w: 640, h: 150 },
  billing: { x: 80, y: 350, w: 640, h: 145 },
  start: { x: 1150, y: 378, w: 430, h: 120 },
  repeat: { x: 80, y: 520, w: 640, h: 250 },
};

// Build the inline style that renders a single crop of the reference image as a
// CSS-cropped window: the full image is scaled so the crop's width fills
// CROP_RENDER_WIDTH, then offset so the crop's top-left sits at the window's
// origin.
const cropStyle = (crop: Crop): string => {
  const scale = CROP_RENDER_WIDTH / crop.w;
  const backgroundWidth = Math.round(REFERENCE_IMAGE_WIDTH * scale);
  const offsetX = -Math.round(crop.x * scale);
  const offsetY = -Math.round(crop.y * scale);
  const windowHeight = Math.round(crop.h * scale);
  return [
    `height:${windowHeight}px`,
    `background-image:url(${orderV2Reference})`,
    `background-size:${backgroundWidth}px auto`,
    `background-position:${offsetX}px ${offsetY}px`,
  ].join(';');
};

// driver.js renders `popover.description` as HTML, so we embed the crop window
// and the instruction as two block spans. Content is static (no user input).
const imageDescription = (crop: Crop, instruction: string): string =>
  `<span class="orderflow-tour-crop" style="${cropStyle(crop)}"></span>` +
  `<span class="orderflow-tour-instruction">${instruction}</span>`;

const entryImageAnchors: TourStep[] = [
  {
    element: tourSelector('entry-client'),
    popover: {
      title: 'Pick the client',
      description: imageDescription(
        ENTRY_IMAGE_CROPS.client,
        'Start typing to search your directory and choose the client you’re ordering for.',
      ),
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: tourSelector('entry-billing'),
    popover: {
      title: 'Choose a billing account',
      description: imageDescription(
        ENTRY_IMAGE_CROPS.billing,
        'Select the billing account to charge — the order can’t start until one with a valid payment method is set.',
      ),
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: tourSelector('entry-start'),
    popover: {
      title: 'Start the order',
      description: imageDescription(
        ENTRY_IMAGE_CROPS.start,
        'Once your client and billing account are set, hit Start order to open the full order wizard.',
      ),
      side: 'top',
      align: 'start',
    },
  },
  {
    element: tourSelector('entry-repeat'),
    // A first-time user has no billing account picked and no saved templates,
    // so this section is not on screen — but the crop above shows exactly what
    // it will look like, so the step is kept (centered) rather than dropped.
    keepWhenAnchorMissing: true,
    popover: {
      title: 'Repeat a past order',
      description: imageDescription(
        ENTRY_IMAGE_CROPS.repeat,
        'Ordered something before? Pick a past order here to prefill the whole thing instantly.',
      ),
      side: 'top',
      align: 'start',
    },
  },
];

/**
 * Auto-run first-entry tour for `/orders-v2/create`. Same anchors as
 * `ENTRY_TOUR_STEPS`, but each popover carries an image crop of the reference
 * mockup. Absent anchors (client for non-tenant admins, repeat orders before a
 * templated billing account is picked) are dropped by `useOrderFlowTour`.
 */
export const ENTRY_IMAGE_TOUR_STEPS: TourStep[] = [
  ...entryImageAnchors,
  helpOutro,
];
